import {acceptHMRUpdate, defineStore} from 'pinia';
import {computed, ref} from 'vue';
import useApi from '~/composables/useApi';
import {useFetchedEntity} from '~/lib/fetched-entity';
import type {
	ExistingResponsibleCommand,
	NewResponsibleCommand,
	ResponsibleDecorator,
	ResponsibleMoveDecorator
} from '~/lib/types/responsibleTypes';
import type {ProblemDetail} from '~/lib/types/fetchErrors';
import {parseProblemDetail} from '~/middleware/api';
import {responsibleCommandLabels} from '~/composables/useResponsibleForm';

export const useResponsiblesStore = defineStore('responsibles', () => {
	const {GET, POST, DELETE} = useApi();

	const responsibles = ref<ResponsibleDecorator[]>([]);
	const loading = ref(false);
	const problemDetail = ref<ProblemDetail>();

	// getActiveOrCurrentResponsible where current responsible is a paramether, should be a computed getter
	const getResponsibleOptions = computed(() => (currentId?: number) => {
		return responsibles.value.filter(responsible => {
			// Always show the responsible that is already assigned to the action
			if (currentId && responsible.id === currentId) {
				return true;
			}
			return responsible.active;
		});
	});

	const getResponsibleById = computed(() => (id: number | string) => {
		const idNumber = Number(id);
		if (isNaN(idNumber)) {
			throw new TypeError('Responsible id should be a number');
		}
		return responsibles.value.find(responsible => responsible.id === idNumber);
	});

	const updateResponsible = async (data: ResponsibleDecorator) => {
		const responsibleIndex = responsibles.value.findIndex((responsible) => responsible.id === data.id);
		if (responsibleIndex === -1) {
			responsibles.value.push(data);
		} else {
			responsibles.value[responsibleIndex] = data;
		}
	};

	const fetchResponsibles = async (companyId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await GET('/responsible/{companyId}', {
			params: {
				path: {companyId}
			}
		});

		loading.value = false;
		if (error || !data) {
			problemDetail.value = error;
			return false;
		}

		responsibles.value = data;
		return true;
	};

	const fetchResponsible = async (companyId: string, responsibleId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await GET('/responsible/{companyId}/{responsibleId}', {
			params: {
				path: {companyId, responsibleId}
			}
		});

		loading.value = false;
		if (error || !data) {
			problemDetail.value = error;
			return false;
		}

		await updateResponsible(data);
		return true;
	};

	const saveResponsible = async (companyId: string, responsible: ExistingResponsibleCommand | NewResponsibleCommand) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (responsible.tag === 'NEW') {
			const {data, error} = await POST('/responsible/{companyId}', {
				params: {
					path: {companyId}
				},
				body: responsible as NewResponsibleCommand
			});

			loading.value = false;
			if (error || !data) {
				problemDetail.value = parseProblemDetail(error, responsibleCommandLabels);
				return false;
			}

			await updateResponsible(data);
			return true;
		}

		const {data, error} = await POST('/responsible/{companyId}/{responsibleId}', {
			params: {
				path: {
					companyId,
					responsibleId: responsible.id.toString()
				}
			},
			body: responsible as ExistingResponsibleCommand
		});

		loading.value = false;
		if (error || !data) {
			problemDetail.value = parseProblemDetail(error, responsibleCommandLabels);
			return false;
		}

		await updateResponsible(data);
		return true;
	};

	const moveActionsOfResponsible = async (companyId: string, fromResponsibleId: string, toResponsibleId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await POST('/responsible/{companyId}/move/{fromResponsibleId}/{toResponsibleId}', {
			params: {
				path: {
					companyId,
					fromResponsibleId,
					toResponsibleId
				}
			}
		});

		loading.value = false;
		if (error || !data) {
			problemDetail.value = error;
			return false;
		}

		await fetchResponsibles(companyId);
		return true;
	};

	const deleteResponsible = async (companyId: string, responsibleId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await DELETE('/responsible/{companyId}/{responsibleId}', {
			params: {
				path: {
					companyId,
					responsibleId
				}
			}
		});

		loading.value = false;
		if (error || !data) {
			problemDetail.value = error;
			return false;
		}

		await fetchResponsibles(companyId);
		return data;
	};

	const updateResponsibleStatus = async (companyId: string, responsibleId: string, status: 'activate' | 'deactivate') => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await POST(`/responsible/{companyId}/{responsibleId}/${status}`, {
			params: {
				path: {
					companyId,
					responsibleId
				}
			}
		});

		loading.value = false;
		if (error || !data) {
			problemDetail.value = error;
			return false;
		}

		await updateResponsible(data);
		return true;
	};

	return {
		loading,
		problemDetail,
		responsibles,
		getResponsibleById,
		getResponsibleOptions,
		fetchResponsible,
		fetchResponsibles,
		saveResponsible,
		moveActionsOfResponsible,
		deleteResponsible,
		updateResponsibleStatus,
		findMovableResponsibles(companyId: string) {
			return useFetchedEntity<ResponsibleMoveDecorator>(async () => await GET(
				'/responsible/{companyId}/move',
				{
					params: {
						path: {companyId}
					}
				}
			));
		}
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useResponsiblesStore, import.meta.hot));
}
